import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  ExternalSettingValueSource,
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  HsaFlatTeamSettingGroupById,
  type HsaFlatTeamSettingGroupId,
} from "./groups";

type HsaSettingInput = SettingRegistryMetadataInput;

function setting(metadata: HsaSettingInput): HsaSettingInput {
  return metadata;
}

function hsaGroup<
  const Group extends HsaFlatTeamSettingGroupId,
  const T extends Record<string, HsaSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const HsaRegistry = {
  ...hsaGroup("hsa.buy_buttons", {
    "hsa/buyButtons/showOnlyHsaPaymentMethod": setting({
      label: "Show only HSA payment method when HSA buy button clicked",
      description:
        "When a shopper enters checkout through a storefront Buy with HSA/FSA button, hide the other payment methods so only HSA/FSA is offered. Turn off to keep all payment methods visible; HSA/FSA is still promoted to the top either way.",
      synonyms: [
        "show only hsa payment method when hsa buy button clicked",
        "hide other payment methods hsa",
        "hsa payment exclusivity",
        "hsa only checkout",
      ],
      valueSemantics: {
        defaultValue: true,
        falseLabel: "All payment methods shown",
        trueLabel: "Only HSA/FSA shown",
      },
    }),
  }),
};

type HsaFlatTeamSettingKey = keyof typeof HsaRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const HsaFlatTeamSettingKeys = Object.keys(
  HsaRegistry,
) as HsaFlatTeamSettingKey[];

export const HsaFlatTeamSettingCatalogDefinitions = HsaFlatTeamSettingKeys.map(
  (key) => {
    const definition = HsaRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: HsaFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  },
) satisfies readonly SettingDefinition<
  HsaFlatTeamSettingKey,
  HsaFlatTeamSettingGroupId,
  TeamSettingsSettingValueSource<HsaFlatTeamSettingKey>
>[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const HsaFlatTeamSettingMetadataByKey = Object.fromEntries(
  HsaFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<HsaFlatTeamSettingKey, SettingMetadata>;

/**
 * The Automatic payout reports controls. Their values live in CockroachDB
 * (`hsa_payout_report_settings`), behind their own RPCs, so they are cataloged
 * with a `crdb` value source rather than as flat team settings: the agent can
 * find and link to them, and reports `adapter_not_implemented` instead of
 * reading or writing them.
 */
const HsaPayoutReportRegistry = hsaGroup("hsa.payout_report", {
  "hsa/payoutReport/enabled": {
    ...setting({
      label: "Send reports",
      description:
        "Emails a CSV of each HSA/FSA payout to the listed recipients the day it is deposited. Nothing is sent while it is off or while no recipient is listed.",
      synonyms: [
        "send payout reports",
        "turn on payout report emails",
        "payout csv emails on",
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Payout reports off",
        trueLabel: "Payout reports on",
      },
    }),
    ref: "hsa_payout_report_settings.enabled",
  },
  "hsa/payoutReport/recipients": {
    ...setting({
      label: "Recipients",
      description:
        "The email addresses that receive each payout report, up to ten. Usually the store's finance or accounting team.",
      synonyms: [
        "payout report recipients",
        "who gets payout reports",
        "payout report email addresses",
      ],
    }),
    ref: "hsa_payout_report_settings.recipients",
  },
  "hsa/payoutReport/payoutFields": {
    ...setting({
      label: "Payout columns",
      description:
        "Columns about the payout itself, such as its ID, amount, and value date, included in each payout report CSV.",
      synonyms: ["payout report payout columns", "payout csv payout fields"],
    }),
    ref: "hsa_payout_report_settings.fields",
  },
  "hsa/payoutReport/orderFields": {
    ...setting({
      label: "Order columns",
      description:
        "Columns about each order in the payout, such as its reference, fees, and net amount, included in each payout report CSV.",
      synonyms: ["payout report order columns", "payout csv order fields"],
    }),
    ref: "hsa_payout_report_settings.fields",
  },
});

type HsaPayoutReportSettingKey = keyof typeof HsaPayoutReportRegistry;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const HsaPayoutReportSettingKeys = Object.keys(
  HsaPayoutReportRegistry,
) as HsaPayoutReportSettingKey[];

export const HsaPayoutReportCatalogDefinitions = HsaPayoutReportSettingKeys.map(
  (key) => {
    const { ref, ...definition } = HsaPayoutReportRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: HsaFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: {
        adapter: "crdb",
        ref,
      } satisfies ExternalSettingValueSource,
    };
  },
) satisfies readonly SettingDefinition<
  HsaPayoutReportSettingKey,
  HsaFlatTeamSettingGroupId
>[];
